import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';
import { playClickSound } from '@/utils/soundUtils';
import { topOverlayElement } from './overlayStack';

export interface MenuItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  badge?: React.ReactNode;
  /** Extra data attributes on the item, e.g. { 'data-tour-id': '…' }. */
  attrs?: Record<`data-${string}`, string>;
  /** Draw a divider above this item. */
  separatorBefore?: boolean;
}

export interface MenuTriggerProps {
  ref: React.Ref<HTMLButtonElement>;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls'?: string;
  onClick: () => void;
}

export interface MenuProps {
  trigger: (props: MenuTriggerProps) => React.ReactElement;
  items: MenuItem[];
  align?: 'start' | 'end';
  'aria-label': string;
  className?: string;
}

/** Button-triggered menu (account, language): arrows, Escape, outside click. */
export default function Menu({ trigger, items, align = 'end', 'aria-label': ariaLabel, className }: MenuProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number }>({ top: 0 });
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    setPos(align === 'end' ? { top: r.bottom + 6, right: Math.max(8, vw - r.right) } : { top: r.bottom + 6, left: Math.max(8, r.left) });
  }, [align]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  useLayoutEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const focusAt = (i: number) => itemRefs.current[(i + items.length) % items.length]?.focus();
  const select = (item: MenuItem) => {
    playClickSound();
    close(true);
    item.onSelect();
  };

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: () => {
          if (open) return close(false);
          place();
          setPortalTarget(topOverlayElement() ?? document.body);
          setOpen(true);
        },
      })}
      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={id}
              role="menu"
              aria-label={ariaLabel}
              style={{ top: pos.top, left: pos.left, right: pos.right }}
              className={cx('fixed z-popover min-w-48 rounded-container border k-panel bg-raised p-1', className)}
            >
              {items.map((item, i) => (
                <React.Fragment key={item.id}>
                {item.separatorBefore ? <div role="separator" className="my-1 h-px bg-edge-subtle" /> : null}
                <button
                  {...item.attrs}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => select(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(i + 1); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); focusAt(i - 1); }
                    else if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
                    else if (e.key === 'End') { e.preventDefault(); focusAt(items.length - 1); }
                    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(item); }
                    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
                    else if (e.key === 'Tab') close(false);
                  }}
                  className={cx(
                    'flex min-h-11 w-full items-center gap-3 rounded-control px-3 text-left font-ui text-body transition-colors',
                    'focus:outline-none focus-visible:bg-selected hover:bg-hover',
                    item.tone === 'danger' ? 'text-danger' : 'text-primary'
                  )}
                >
                  {item.icon ? <span aria-hidden className="inline-flex text-secondary [&>svg]:size-4.5">{item.icon}</span> : null}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.badge}
                </button>
                </React.Fragment>
              ))}
            </div>,
            portalTarget ?? document.body
          )
        : null}
    </>
  );
}
